import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import LightPresetControl from '../components/MapboxGL/LightPresetControl'
import { changeLightPreset } from '../redux/slices/Map/map'

export const useLightPresetControl = ({
  map,
  enabled = false,
  position = 'top-left',
}) => {
  const dispatch = useDispatch()
  const controlRef = useRef(null)
  const controlAddedRef = useRef(false)
  const lastPresetRef = useRef(null)
  const addControlTimerRef = useRef(null)
  const applyPresetTimerRef = useRef(null)

  const lightPresetRedux = useSelector(state => state.mapState.lightPreset.current)
  const lightPresetReduxRef = useRef(lightPresetRedux)
  lightPresetReduxRef.current = lightPresetRedux
  const isStyleReady = useCallback(() => {
    if (!map) return false
    try {
      return Boolean(map.getStyle?.()) && map.isStyleLoaded()
    } catch {
      return false
    }
  }, [map])

  useEffect(() => {
    if (!enabled || !map) return

    const lightPresetStored = localStorage.getItem('ArbolEc_lightPreset')
    const reduxPreset = lightPresetReduxRef.current

    if (lightPresetStored && lightPresetStored !== reduxPreset) {
      dispatch(changeLightPreset(lightPresetStored))
      lastPresetRef.current = lightPresetStored
    } else {
      lastPresetRef.current = reduxPreset
    }
  }, [enabled, map, dispatch])

  useEffect(() => {
    if (!map || !enabled || controlAddedRef.current) return

    let cancelled = false

    const addControlWhenReady = () => {
      if (cancelled || controlAddedRef.current) return

      if (!isStyleReady()) {
        addControlTimerRef.current = setTimeout(addControlWhenReady, 100)
        return
      }

      try {
        const initialPreset = lastPresetRef.current || lightPresetReduxRef.current || 'day'

        const ctrl = new LightPresetControl(initialPreset)
        controlRef.current = ctrl
        controlAddedRef.current = true

        ctrl.onPresetChange = preset => {
          dispatch(changeLightPreset(preset))
        }

        map.addControl(ctrl, position)

        try {
          map.setConfigProperty('basemap', 'lightPreset', initialPreset)
        } catch (error) {
          console.warn('[useLightPresetControl] Error aplicando preset inicial:', error)
        }
      } catch (error) {
        console.warn('[useLightPresetControl] Error agregando control:', error)
        controlAddedRef.current = false
      }
    }

    addControlWhenReady()

    return () => {
      cancelled = true
      if (addControlTimerRef.current) {
        clearTimeout(addControlTimerRef.current)
        addControlTimerRef.current = null
      }

      if (controlRef.current && controlAddedRef.current) {
        try {
          map.removeControl(controlRef.current)
        } catch (error) {
          console.warn('[useLightPresetControl] Error removiendo control:', error)
        }
        controlRef.current = null
        controlAddedRef.current = false
      }
    }
  }, [map, enabled, position, dispatch, isStyleReady])

  useEffect(() => {
    if (!map || !lightPresetRedux || !controlAddedRef.current) return
    if (lastPresetRef.current === lightPresetRedux) return

    lastPresetRef.current = lightPresetRedux

    localStorage.setItem('ArbolEc_lightPreset', lightPresetRedux)

    if (controlRef.current) {
      controlRef.current.updatePreset(lightPresetRedux)
    }

    let cancelled = false

    const applyToMap = () => {
      if (cancelled) return

      if (!isStyleReady()) {
        applyPresetTimerRef.current = setTimeout(applyToMap, 100)
        return
      }

      try {
        map.setConfigProperty('basemap', 'lightPreset', lightPresetRedux)
      } catch (error) {
        console.warn('[useLightPresetControl] Error aplicando preset:', error)
      }
    }

    applyToMap()

    return () => {
      cancelled = true
      if (applyPresetTimerRef.current) {
        clearTimeout(applyPresetTimerRef.current)
        applyPresetTimerRef.current = null
      }
    }
  }, [lightPresetRedux, map, isStyleReady])

  return { controlRef }
}
